import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Lock, LogOut, Loader2 } from "lucide-react";
import { BRAND } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Dashboard() {
  const isAdmin = window.location.pathname.startsWith("/admin");
  const [token, setToken] = useState(() => localStorage.getItem("harver_admin") || "");
  const [input, setInput] = useState("");
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("idle"); // idle | loading | error | ok

  const load = async (secret) => {
    setStatus("loading");
    try {
      const res = await fetch(`${API}/admin/sales`, { headers: { "X-Admin-Token": secret } });
      if (res.status === 401) { setStatus("error"); localStorage.removeItem("harver_admin"); setToken(""); return; }
      const d = await res.json();
      setData(d); setStatus("ok");
    } catch (e) { setStatus("error"); }
  };

  useEffect(() => {
    if (isAdmin && token) load(token);
    // eslint-disable-next-line
  }, []);

  if (!isAdmin) return null;

  const submit = (e) => {
    e.preventDefault();
    localStorage.setItem("harver_admin", input);
    setToken(input);
    load(input);
  };
  const logout = () => { localStorage.removeItem("harver_admin"); setToken(""); setData(null); setStatus("idle"); };
  const money = (c) => `$${(c / 100).toFixed(2)}`;

  return (
    <div className="fixed inset-0 z-[10002] bg-[color:var(--void)] overflow-auto" data-testid="admin-dashboard">
      <div className="grain-overlay" aria-hidden />
      {(!token || status === "error") ? (
        <div className="min-h-full flex items-center justify-center px-6">
          <motion.form onSubmit={submit} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            className="glass-strong rounded-sm p-9 w-full max-w-sm text-center" data-testid="admin-login">
            <Lock size={26} className="mx-auto text-white/70 mb-5" />
            <p className="font-display chrome-text text-2xl mb-1">{BRAND.name}</p>
            <p className="track-label text-white/40 mb-7">SALES DASHBOARD</p>
            <input type="password" value={input} onChange={(e) => setInput(e.target.value)} placeholder="Admin password"
              data-testid="admin-password"
              className="w-full bg-transparent border border-white/20 px-4 py-3.5 text-white placeholder:text-white/30 text-sm focus:outline-none focus:border-white mb-3" />
            {status === "error" && <p className="text-red-300/80 text-xs mb-3" data-testid="admin-error">Incorrect password.</p>}
            <button type="submit" data-testid="admin-submit" className="w-full py-3.5 bg-white text-black track-label hover:bg-white/80 transition-colors">
              {status === "loading" ? <Loader2 size={16} className="animate-spin mx-auto" /> : "ENTER"}
            </button>
            <a href="/" className="block mt-5 track-label text-white/40 hover:text-white">← BACK TO SITE</a>
          </motion.form>
        </div>
      ) : (
        <div className="max-w-[1100px] mx-auto px-5 md:px-10 py-12">
          <div className="flex items-center justify-between mb-10">
            <div>
              <p className="font-display chrome-text text-3xl">{BRAND.name}</p>
              <p className="track-label text-white/40 mt-2">SALES DASHBOARD</p>
            </div>
            <button onClick={logout} data-testid="admin-logout" className="flex items-center gap-2 track-label text-white/50 hover:text-white">
              <LogOut size={14} /> SIGN OUT
            </button>
          </div>

          {status === "loading" && <Loader2 className="animate-spin text-white/60" />}

          {data && (
            <>
              <div className="grid grid-cols-2 gap-4 md:gap-6 mb-10">
                <div className="glass rounded-sm p-6" data-testid="stat-earnings">
                  <p className="track-label text-white/40 mb-3">TOTAL EARNINGS</p>
                  <p className="font-display steel-text text-4xl md:text-5xl">{money(data.gross_cents)}</p>
                </div>
                <div className="glass rounded-sm p-6" data-testid="stat-orders">
                  <p className="track-label text-white/40 mb-3">ORDERS</p>
                  <p className="font-display steel-text text-4xl md:text-5xl">{data.count}</p>
                </div>
              </div>

              <div className="glass rounded-sm overflow-hidden">
                <div className="grid grid-cols-12 px-5 py-4 border-b border-white/10 track-label text-white/40">
                  <div className="col-span-5">ITEM</div>
                  <div className="col-span-4 hidden md:block">BUYER</div>
                  <div className="col-span-2 md:col-span-2 text-right">AMOUNT</div>
                  <div className="col-span-5 md:col-span-1 text-right">DATE</div>
                </div>
                {data.orders.length === 0 && <p className="px-5 py-8 text-white/40 font-body text-sm">No sales yet.</p>}
                {data.orders.map((o, i) => (
                  <div key={o.session_id + i} className="grid grid-cols-12 px-5 py-4 border-b border-white/5 items-center font-body text-sm text-white/75" data-testid="sale-row">
                    <div className="col-span-5 truncate pr-2">{o.item}</div>
                    <div className="col-span-4 hidden md:block truncate text-white/50">{o.email || "\u2014"}</div>
                    <div className="col-span-2 text-right">{money(o.amount)}</div>
                    <div className="col-span-5 md:col-span-1 text-right text-white/40 text-xs">{(o.date || "").slice(0, 10)}</div>
                  </div>
                ))}
              </div>
              <a href="/" className="inline-block mt-8 track-label text-white/40 hover:text-white">← BACK TO SITE</a>
            </>
          )}
        </div>
      )}
    </div>
  );
}
